import { useState } from "react";
import { Menu, MenuButton, MenuItems } from "@headlessui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/20/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Color mapping based on event type
const getColor = (eventType) => {
  switch (eventType) {
    case "estimateDueDate":
      return "bg-blue-100 text-blue-700";
    case "proposalSentDate":
      return "bg-green-100 text-green-700";
    case "activity":
      return "bg-purple-100 text-purple-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

// Extract initials from displayName
const getInitials = (displayName) => {
  if (!displayName) return "NA";
  const names = displayName.trim().split(/\s+/);
  if (names.length === 0) return "NA";
  const first = names[0][0] || "";
  const last = names[names.length - 1][0] || "";
  return `${first}${last}`.toUpperCase();
};

// Format date for display
const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export default function OpportunitiesCalendar({
  opportunities,
  navigate,
  visibleEventTypes,
  users,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Generate days for the current month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    // Add days from previous month to fill first week
    const startDay = firstDay.getDay();
    for (let i = startDay; i > 0; i--) {
      const prevDay = new Date(year, month, 1 - i);
      days.push({
        date: prevDay.toISOString().split("T")[0],
        isCurrentMonth: false,
        events: [],
      });
    }

    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const day = new Date(year, month, i);
      const dayString = day.toISOString().split("T")[0];
      const events = [];

      // Add estimate due date events
      if (visibleEventTypes.estimateDueDate) {
        opportunities.forEach((opp) => {
          if (
            opp.estimateDueDate &&
            new Date(opp.estimateDueDate).toISOString().split("T")[0] ===
              dayString
          ) {
            events.push({
              id: opp._id,
              name: opp.title,
              userId: opp.estimator?._id,
              userInitials: getInitials(opp.estimator?.displayName),
              userColor: getColor("estimateDueDate"),
              eventType: "estimateDueDate",
              href: `/Opportunities/${opp._id}`,
            });
          }
        });
      }

      // Add proposal sent date events
      if (visibleEventTypes.proposalSentDate) {
        opportunities.forEach((opp) => {
          if (
            opp.proposalSentDate &&
            new Date(opp.proposalSentDate).toISOString().split("T")[0] ===
              dayString
          ) {
            events.push({
              id: opp._id,
              name: opp.title,
              userId: opp.estimator?._id,
              userInitials: getInitials(opp.estimator?.displayName),
              userColor: getColor("proposalSentDate"),
              eventType: "proposalSentDate",
              href: `/Opportunities/${opp._id}`,
            });
          }
        });
      }

      // Add activity events
      if (visibleEventTypes.activity) {
        opportunities.forEach((opp) => {
          (opp.activities || []).forEach((activity) => {
            if (
              activity.dueDate &&
              new Date(activity.dueDate).toISOString().split("T")[0] ===
                dayString
            ) {
              const assignedUser = users.find(
                (user) =>
                  user._id === (activity.assignedTo?._id || activity.assignedTo)
              );
              events.push({
                id: `${opp._id}-${activity._id}`,
                name: activity.title,
                userId:
                  activity.assignedTo?._id ||
                  activity.createdBy?._id ||
                  activity.assignedTo,
                userInitials: getInitials(
                  assignedUser?.displayName ||
                    activity.assignedTo?.displayName ||
                    activity.createdBy?.displayName
                ),
                userColor: getColor("activity"),
                eventType: "activity",
                dueDate: activity.dueDate,
                activityId: activity._id,
                href: `/Opportunities/${opp._id}?tab=Activities&activityId=${activity._id}`,
              });
            }
          });
        });
      }

      days.push({
        date: dayString,
        isCurrentMonth: true,
        isToday:
          Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()) ===
          Date.UTC(
            new Date().getFullYear(),
            new Date().getMonth(),
            new Date().getDate()
          ),
        events,
      });
    }

    // Add days from next month to fill last week
    const endDay = lastDay.getDay();
    for (let i = 1; i < 7 - endDay; i++) {
      const nextDay = new Date(year, month + 1, i);
      days.push({
        date: nextDay.toISOString().split("T")[0],
        isCurrentMonth: false,
        events: [],
      });
    }

    return days;
  };

  const days = getDaysInMonth(currentDate);
  const selectedDay = days.find((day) => day.isToday) || days[0];

  const handlePreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  return (
    <div className="lg:flex lg:h-full lg:flex-col mx-auto">
      {/* Calendar Header */}
      <header className="flex items-center justify-between border-b border-gray-700 px-4 py-4 bg-gray-800 lg:flex-none">
        <div className="flex items-center space-x-4">
          <h1 className="text-base font-semibold text-white">
            <time dateTime={currentDate.toISOString().split("T")[0]}>
              {currentDate.toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </time>
          </h1>
        </div>
        <div className="flex items-center">
          <div className="relative flex items-center rounded-md bg-gray-700 shadow-sm md:items-stretch">
            <button
              type="button"
              onClick={handlePreviousMonth}
              className="flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-600 pr-1 text-gray-300 hover:text-gray-200 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-600"
            >
              <span className="sr-only">Previous month</span>
              <ChevronLeftIcon className="size-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={handleToday}
              className="hidden border-y border-gray-600 px-3.5 text-sm font-semibold text-gray-200 hover:bg-gray-600 focus:relative md:block"
            >
              Month
            </button>
            <span className="relative -mx-px h-5 w-px bg-gray-600 md:hidden" />
            <button
              type="button"
              onClick={handleNextMonth}
              className="flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-600 pl-1 text-gray-300 hover:text-gray-200 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-600"
            >
              <span className="sr-only">Next month</span>
              <ChevronRightIcon className="size-5" aria-hidden="true" />
            </button>
          </div>
          <Menu as="div" className="relative ml-6 md:hidden">
            <MenuButton className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-300 hover:text-gray-200">
              <span className="sr-only">Open menu</span>
              <EllipsisHorizontalIcon className="size-5" aria-hidden="true" />
            </MenuButton>
            <MenuItems
              transition
              className="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-gray-700 overflow-hidden rounded-md bg-gray-800 shadow-lg ring-1 ring-black/5 focus:outline-none data-closed:scale-95 data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
            >
              <div className="py-1">
                <button
                  onClick={handleToday}
                  className="block px-4 py-2 text-sm text-gray-300 data-focus:bg-gray-700 data-focus:text-gray-200 data-focus:outline-none"
                >
                  Go to today
                </button>
              </div>
            </MenuItems>
          </Menu>
        </div>
      </header>

      {/* Calendar Grid */}
      <div className="shadow-sm ring-1 ring-black/5 lg:flex lg:flex-auto lg:flex-col ">
        <div className="grid grid-cols-7 gap-px border-b border-gray-800 bg-gray-800 text-center text-xs font-semibold text-gray-300 lg:flex-none ">
          <div className="bg-gray-700 py-2 border-r border-gray-800">
            M<span className="sr-only sm:not-sr-only">on</span>
          </div>
          <div className="bg-gray-700 py-2 border-r border-gray-800">
            T<span className="sr-only sm:not-sr-only">ue</span>
          </div>
          <div className="bg-gray-700 py-2 border-r border-gray-800">
            W<span className="sr-only sm:not-sr-only">ed</span>
          </div>
          <div className="bg-gray-700 py-2 border-r border-gray-800">
            T<span className="sr-only sm:not-sr-only">hu</span>
          </div>
          <div className="bg-gray-700 py-2 border-r border-gray-800">
            F<span className="sr-only sm:not-sr-only">ri</span>
          </div>
          <div className="bg-gray-700 py-2 border-r border-gray-800">
            S<span className="sr-only sm:not-sr-only">at</span>
          </div>
          <div className="bg-gray-700 py-2">
            S<span className="sr-only sm:not-sr-only">un</span>
          </div>
        </div>
        <div className="flex bg-gray-800 text-xs text-gray-300 lg:flex-auto ">
          <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-x-px lg:gap-y-0.5 lg:border-t lg:border-gray-800 ">
            {days.map((day, index) => (
              <div
                key={day.date}
                className={classNames(
                  day.isCurrentMonth
                    ? "bg-gray-800"
                    : "bg-gray-700 text-gray-500",
                  "relative px-2 py-2 border-b",
                  index % 7 !== 6 ? "border-r border-gray-600" : "",
                  index < days.length - 7
                    ? "border-b border-gray-600"
                    : "border-b border-gray-600"
                )}
              >
                <time
                  dateTime={day.date}
                  className={classNames(
                    day.isToday
                      ? "flex size-6 items-center justify-center rounded-full bg-orange-400 font-semibold text-white"
                      : "font-medium",
                    "mb-1 text-gray-300"
                  )}
                >
                  {day.date.split("-").pop().replace(/^0/, "")}
                </time>
                {day.events.length > 0 && (
                  <ol className="mt-1 space-y-1">
                    {day.events.slice(0, 2).map((event) => (
                      <li key={`${event.id}-${event.eventType}`}>
                        <button
                          onClick={() => navigate(event.href)}
                          className={classNames(
                            event.userColor,
                            "group flex w-full items-center rounded-full px-2 py-0.5"
                          )}
                          aria-label={`${
                            event.eventType === "estimateDueDate"
                              ? "Estimate Due"
                              : event.eventType === "proposalSentDate"
                              ? "Proposal Sent"
                              : "Activity"
                          }: ${event.name}, By: ${event.userInitials}`}
                        >
                          <svg
                            viewBox="0 0 6 6"
                            aria-hidden="true"
                            className={classNames(
                              event.eventType === "estimateDueDate"
                                ? "fill-blue-500"
                                : event.eventType === "proposalSentDate"
                                ? "fill-green-500"
                                : "fill-purple-500",
                              "size-1.5 mr-1"
                            )}
                          >
                            <circle r={3} cx={3} cy={3} />
                          </svg>
                          <p className="flex-auto truncate font-medium text-xs">
                            {event.name}
                          </p>
                          <span className="ml-2 font-medium text-xs">
                            {event.userInitials}
                          </span>
                        </button>
                      </li>
                    ))}
                    {day.events.length > 2 && (
                      <li className="text-gray-400">
                        + {day.events.length - 2} more
                      </li>
                    )}
                  </ol>
                )}
              </div>
            ))}
          </div>
          <div className="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
            {days.map((day, index) => (
              <button
                key={day.date}
                type="button"
                className={classNames(
                  day.isCurrentMonth ? "bg-gray-800" : "bg-gray-900",
                  day.isToday && "font-semibold",
                  day.isToday && "text-orange-400",
                  !day.isCurrentMonth && !day.isToday && "text-gray-500",
                  "flex h-14 flex-col px-3 py-2 hover:bg-gray-700 focus:z-10",
                  index % 7 !== 6 ? "border-r border-orange-400" : "",
                  index < days.length - 7 ? "border-b border-orange-400" : ""
                )}
              >
                <time
                  dateTime={day.date}
                  className={classNames(
                    day.isToday &&
                      "flex size-6 items-center justify-center rounded-full bg-orange-400 text-white",
                    "ml-auto text-gray-300"
                  )}
                >
                  {day.date.split("-").pop().replace(/^0/, "")}
                </time>
                <span className="sr-only">{day.events.length} events</span>
                {day.events.length > 0 && (
                  <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
                    {day.events.map((event) => (
                      <span
                        key={`${event.id}-${event.eventType}`}
                        className={classNames(
                          event.eventType === "estimateDueDate"
                            ? "bg-blue-300"
                            : event.eventType === "proposalSentDate"
                            ? "bg-green-300"
                            : "bg-purple-300",
                          "mx-0.5 mb-1 size-1.5 rounded-full"
                        )}
                      />
                    ))}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Event List */}
      {selectedDay?.events.length > 0 && (
        <div className="px-4 py-10 sm:px-6 lg:hidden bg-gray-800">
          <ol className="divide-y divide-orange-400 rounded-lg bg-gray-800 text-sm shadow-sm ring-1 ring-black/5">
            {selectedDay.events.map((event) => (
              <li
                key={`${event.id}-${event.eventType}`}
                className="group flex p-4 pr-6 focus-within:bg-gray-700 hover:bg-gray-700"
              >
                <div className="flex-auto">
                  <p
                    className={classNames(
                      event.userColor,
                      "inline-flex items-center gap-x-1.5 rounded-full px-2 py-0.5 font-semibold text-xs"
                    )}
                    aria-label={`${
                      event.eventType === "estimateDueDate"
                        ? "Estimate Due"
                        : event.eventType === "proposalSentDate"
                        ? "Proposal Sent"
                        : "Activity"
                    }: ${event.name}, By: ${event.userInitials}`}
                  >
                    <svg
                      viewBox="0 0 6 6"
                      aria-hidden="true"
                      className={classNames(
                        event.eventType === "estimateDueDate"
                          ? "fill-blue-500"
                          : event.eventType === "proposalSentDate"
                          ? "fill-green-500"
                          : "fill-purple-500",
                        "size-1.5"
                      )}
                    >
                      <circle r={3} cx={3} cy={3} />
                    </svg>
                    {event.name}
                    <span className="ml-2">{event.userInitials}</span>
                  </p>
                  {event.eventType === "activity" && (
                    <p className="mt-1 text-xs text-gray-400">
                      Due: {formatDate(event.dueDate)}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => navigate(event.href)}
                  className="ml-6 flex-none self-center rounded-full bg-blue-100 px-3 py-1 font-semibold text-gray-900 hover:bg-blue-200"
                >
                  View<span className="sr-only">, {event.name}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
